/**
 * Motor Universal de Escenas Cinematográficas para VEYRA (Frontend).
 *
 * Reconoce la sintaxis @Escena N [Xs] en la caja principal de prompt,
 * sincroniza las escenas visuales de forma bidireccional y adapta
 * las secuencias cinematográficas a las capacidades del modelo seleccionado.
 */

// Regex para detectar encabezados de escena (@Escena 1 [3s], @escena 2 [2s], etc.)
export const SCENE_HEADER_REGEX = /@(?:escena|scene|toma|shot)\s+(\d+)\s*(?:\[\s*(\d+)\s*s(?:eg(?:undos)?)?\s*\])?/gi;

export const STRUCTURED_MODELS = new Set(["kling-v3", "kling-3.0"]);

export const UNSUPPORTED_MODELS = new Set([
  "kling-v2-6-motion-control",
  "flux-3-video",
  "hailuo-02",
  "minimax-hailuo-02",
  "hailuo-2-3",
  "minimax-hailuo-2.3",
  "hailuo-2-3-fast",
  "minimax-hailuo-2.3-fast",
  "hailuo-3",
  "minimax-h3",
]);

export const MODEL_CAPABILITIES = {
  "kling-v3": {
    strategy: "structured",
    label: "Escenas estructuradas (APIMart)",
    badgeTone: "sky",
    maxScenes: 6,
    minSceneDuration: 1,
    maxSceneDuration: 10,
    durations: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
    maxPromptPerScene: 512,
    description: "Cada escena se procesa de forma nativa e independiente mediante multi_prompt.",
  },
  "seedance-2.5": {
    strategy: "temporal_prompt",
    label: "Escenas dirigidas por prompt",
    badgeTone: "amber",
    maxScenes: 6,
    minSceneDuration: 1,
    maxSceneDuration: 10,
    durations: [5, 10],
    maxPromptPerScene: 1000,
    description: "Secuencia temporal con continuidad visual y roles multi-imagen.",
  },
  "seedance-2-5": {
    strategy: "temporal_prompt",
    label: "Escenas dirigidas por prompt",
    badgeTone: "amber",
    maxScenes: 6,
    minSceneDuration: 1,
    maxSceneDuration: 10,
    durations: [5, 10],
    maxPromptPerScene: 1000,
    description: "Secuencia temporal con continuidad visual y roles multi-imagen.",
  },
  "seedance-2.0": {
    strategy: "temporal_prompt",
    label: "Escenas dirigidas por prompt",
    badgeTone: "amber",
    maxScenes: 6,
    minSceneDuration: 1,
    maxSceneDuration: 10,
    durations: [5, 10],
    maxPromptPerScene: 1000,
    description: "Secuencia temporal continua con excelente retención de sujeto.",
  },
  "seedance-2-0": {
    strategy: "temporal_prompt",
    label: "Escenas dirigidas por prompt",
    badgeTone: "amber",
    maxScenes: 6,
    minSceneDuration: 1,
    maxSceneDuration: 10,
    durations: [5, 10],
    maxPromptPerScene: 1000,
    description: "Secuencia temporal continua con excelente retención de sujeto.",
  },
  "seedance-2-0-fast": {
    strategy: "temporal_prompt",
    label: "Escenas dirigidas por prompt",
    badgeTone: "amber",
    maxScenes: 6,
    minSceneDuration: 1,
    maxSceneDuration: 10,
    durations: [5, 10],
    maxPromptPerScene: 1000,
    description: "Secuencia rápida con marcas temporales cinematográficas.",
  },
  "seedance-1-5-pro": {
    strategy: "temporal_prompt",
    label: "Escenas dirigidas por prompt",
    badgeTone: "amber",
    maxScenes: 6,
    minSceneDuration: 1,
    maxSceneDuration: 10,
    durations: [5, 10],
    maxPromptPerScene: 1000,
    description: "Secuencia temporal con fotograma de referencia y continuidad.",
  },
  "seedance-1-0-pro": {
    strategy: "temporal_prompt",
    label: "Escenas dirigidas por prompt",
    badgeTone: "amber",
    maxScenes: 6,
    minSceneDuration: 1,
    maxSceneDuration: 10,
    durations: [5, 10],
    maxPromptPerScene: 1000,
    description: "Secuencia temporal económica.",
  },
  "seedance-1-0-pro-fast": {
    strategy: "temporal_prompt",
    label: "Escenas dirigidas por prompt",
    badgeTone: "amber",
    maxScenes: 6,
    minSceneDuration: 1,
    maxSceneDuration: 10,
    durations: [5, 10],
    maxPromptPerScene: 1000,
    description: "Secuencia temporal ultra-económica.",
  },
  "wan2.7": {
    strategy: "temporal_prompt",
    label: "Escenas dirigidas por prompt",
    badgeTone: "amber",
    maxScenes: 8,
    minSceneDuration: 1,
    maxSceneDuration: 15,
    durations: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
    maxPromptPerScene: 1000,
    description: "Soporta secuencias flexibles de 2 a 15 segundos con cortes dinámicos.",
  },
  "wan-3-0": {
    strategy: "temporal_prompt",
    label: "Escenas dirigidas por prompt",
    badgeTone: "amber",
    maxScenes: 8,
    minSceneDuration: 1,
    maxSceneDuration: 15,
    durations: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
    maxPromptPerScene: 1000,
    description: "Secuencia temporal con audio nativo sincronizado.",
  },
  "wan-3-0-prime": {
    strategy: "temporal_prompt",
    label: "Escenas dirigidas por prompt",
    badgeTone: "amber",
    maxScenes: 8,
    minSceneDuration: 1,
    maxSceneDuration: 15,
    durations: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
    maxPromptPerScene: 1000,
    description: "Mayor fidelidad visual en transiciones entre escenas.",
  },
  "veo-3-1": {
    strategy: "temporal_prompt",
    label: "Escenas dirigidas por prompt",
    badgeTone: "amber",
    maxScenes: 4,
    minSceneDuration: 1,
    maxSceneDuration: 8,
    durations: [4, 6, 8],
    maxPromptPerScene: 1000,
    description: "Secuencias cinematográficas fotorrealistas en 4s, 6s u 8s.",
  },
  "vidu-q3": {
    strategy: "temporal_prompt",
    label: "Escenas dirigidas por prompt",
    badgeTone: "amber",
    maxScenes: 4,
    minSceneDuration: 1,
    maxSceneDuration: 8,
    durations: [4, 8],
    maxPromptPerScene: 1000,
    description: "Secuencias dinámicas con transiciones en 4s u 8s.",
  },
  "vidu-q4": {
    strategy: "temporal_prompt",
    label: "Escenas dirigidas por prompt",
    badgeTone: "amber",
    maxScenes: 3,
    minSceneDuration: 1,
    maxSceneDuration: 5,
    durations: [5],
    maxPromptPerScene: 1000,
    description: "Secuencias compactas de 5 segundos.",
  },
  "pixverse-v6": {
    strategy: "temporal_prompt",
    label: "Escenas dirigidas por prompt",
    badgeTone: "amber",
    maxScenes: 4,
    minSceneDuration: 1,
    maxSceneDuration: 8,
    durations: [5, 8],
    maxPromptPerScene: 1000,
    description: "Secuencias con soporte de audio cinemático.",
  },
  "gemini-omni-1-1-flash": {
    strategy: "temporal_prompt",
    label: "Escenas dirigidas por prompt",
    badgeTone: "amber",
    maxScenes: 3,
    minSceneDuration: 1,
    maxSceneDuration: 5,
    durations: [5],
    maxPromptPerScene: 1000,
    description: "Secuencias breves de 5 segundos.",
  },
  "grok-video": {
    strategy: "temporal_prompt",
    label: "Escenas dirigidas por prompt",
    badgeTone: "amber",
    maxScenes: 5,
    minSceneDuration: 1,
    maxSceneDuration: 10,
    durations: [5, 10],
    maxPromptPerScene: 1000,
    description: "Comprensión semántica de marcas de corte.",
  },
  "happyhorse-1-0": {
    strategy: "temporal_prompt",
    label: "Escenas dirigidas por prompt",
    badgeTone: "amber",
    maxScenes: 5,
    minSceneDuration: 1,
    maxSceneDuration: 10,
    durations: [5, 10],
    maxPromptPerScene: 1000,
    description: "Secuencias continuas mediante lenguaje de dirección.",
  },
  "kling-2-6": {
    strategy: "temporal_prompt",
    label: "Escenas dirigidas por prompt",
    badgeTone: "amber",
    maxScenes: 5,
    minSceneDuration: 1,
    maxSceneDuration: 10,
    durations: [5, 10],
    maxPromptPerScene: 1000,
    description: "Secuencia interpretada por marcas temporales en prompt.",
  },
  "kling-3-0-turbo": {
    strategy: "temporal_prompt",
    label: "Escenas dirigidas por prompt",
    badgeTone: "amber",
    maxScenes: 5,
    minSceneDuration: 1,
    maxSceneDuration: 10,
    durations: [5, 10],
    maxPromptPerScene: 1000,
    description: "Generación acelerada con directivas cinematográficas.",
  },
  "kling-video-o1": {
    strategy: "temporal_prompt",
    label: "Escenas dirigidas por prompt",
    badgeTone: "amber",
    maxScenes: 5,
    minSceneDuration: 1,
    maxSceneDuration: 10,
    durations: [5, 10],
    maxPromptPerScene: 1000,
    description: "Secuencias interpretadas por razonamiento semántico.",
  },
};

export const DEFAULT_UNSUPPORTED = {
  strategy: "unsupported",
  label: "Toma única tradicional",
  badgeTone: "zinc",
  maxScenes: 1,
  durations: [5],
  description: "Este modelo procesa únicamente una toma continua por generación.",
};

/**
 * Consulta la estrategia y capacidades de escenas de un modelo.
 */
export function getModelSceneCapabilities(modelId) {
  if (!modelId) return DEFAULT_UNSUPPORTED;
  const mClean = String(modelId).toLowerCase().trim();

  if (UNSUPPORTED_MODELS.has(mClean)) {
    return DEFAULT_UNSUPPORTED;
  }

  if (MODEL_CAPABILITIES[mClean]) {
    return MODEL_CAPABILITIES[mClean];
  }

  // Búsqueda por subcadena
  for (const [k, v] of Object.entries(MODEL_CAPABILITIES)) {
    if (k.includes(mClean) || mClean.includes(k)) {
      return v;
    }
  }

  return DEFAULT_UNSUPPORTED;
}

/**
 * Analiza el texto de la caja de prompt y extrae las escenas declaradas.
 */
export function parseScenesFromPrompt(rawText) {
  const text = (rawText || "").trim();
  if (!text) {
    return {
      hasScenes: false,
      preamble: "",
      scenes: [],
      totalDuration: 0,
      count: 0,
    };
  }

  const regex = new RegExp(SCENE_HEADER_REGEX.source, "gi");
  const matches = [];
  let m;
  while ((m = regex.exec(text)) !== null) {
    matches.push({
      index: m.index,
      lastIndex: regex.lastIndex,
      sceneNum: m[1],
      duration: m[2],
    });
  }

  if (matches.length === 0) {
    return {
      hasScenes: false,
      preamble: text,
      scenes: [],
      totalDuration: 0,
      count: 0,
    };
  }

  const preamble = text.substring(0, matches[0].index).trim();
  const scenes = [];

  for (let i = 0; i < matches.length; i++) {
    const curr = matches[i];
    const nextStart = i + 1 < matches.length ? matches[i + 1].index : text.length;
    const body = text.substring(curr.lastIndex, nextStart).trim();

    const idx = curr.sceneNum ? parseInt(curr.sceneNum, 10) : i + 1;
    const dur = curr.duration ? parseInt(curr.duration, 10) : 3;

    scenes.push({
      index: idx,
      duration: isNaN(dur) || dur <= 0 ? 3 : dur,
      prompt: body,
    });
  }

  const totalDuration = scenes.reduce((acc, s) => acc + s.duration, 0);

  return {
    hasScenes: true,
    preamble,
    scenes,
    totalDuration,
    count: scenes.length,
  };
}

/**
 * Reconstruye el prompt completo a partir del preámbulo y el array de escenas.
 */
export function reconstructPromptWithScenes(preamble, scenes) {
  const chunks = [];
  const cleanPreamble = (preamble || "").trim();
  if (cleanPreamble) {
    chunks.push(cleanPreamble);
  }

  (scenes || []).forEach((s, idx) => {
    const sceneIndex = s.index || idx + 1;
    const sceneDur = s.duration || 3;
    const scenePrompt = (s.prompt || "").trim();
    chunks.push(`@Escena ${sceneIndex} [${sceneDur}s]\n${scenePrompt}`);
  });

  return chunks.join("\n\n");
}

/**
 * Compila las escenas a un prompt cinematográfico con marcas temporales.
 */
export function compileScenesToTemporalPrompt(scenes, preamble = "") {
  const chunks = [];
  const cleanPreamble = (preamble || "").trim();
  if (cleanPreamble) {
    chunks.push(cleanPreamble);
  }

  let currentSec = 0;
  (scenes || []).forEach((s, idx) => {
    const dur = Math.max(1, parseInt(s.duration, 10) || 3);
    const endSec = currentSec + dur;

    const tStart = `${String(Math.floor(currentSec / 60)).padStart(2, "0")}:${String(currentSec % 60).padStart(2, "0")}`;
    const tEnd = `${String(Math.floor(endSec / 60)).padStart(2, "0")}:${String(endSec % 60).padStart(2, "0")}`;

    const sceneNum = s.index || idx + 1;
    const scenePrompt = (s.prompt || "").trim();
    const transitionPrefix = sceneNum === 1 ? "Scene 1:" : `Cut to Scene ${sceneNum}:`;

    chunks.push(`[${tStart}–${tEnd}] ${transitionPrefix} ${scenePrompt}`);
    currentSec = endSec;
  });

  return chunks.join("\n\n");
}

/**
 * Valida un conjunto de escenas frente a las capacidades del modelo seleccionado.
 */
export function validateScenesForModel(modelId, scenes, selectedDuration) {
  const caps = getModelSceneCapabilities(modelId);
  const errors = [];
  const warnings = [];

  if (caps.strategy === "unsupported") {
    warnings.push(
      `El modelo '${modelId}' procesa tomas continuas tradicionales. Las etiquetas de escena se conservarán en el texto, pero no se generará división de tomas.`
    );
    return {
      isValid: true,
      strategy: "unsupported",
      errors,
      warnings,
      totalDuration: selectedDuration || 5,
    };
  }

  const totalDuration = (scenes || []).reduce((acc, s) => acc + (Number(s.duration) || 0), 0);

  // Validar número de escenas
  if (scenes.length > caps.maxScenes) {
    errors.push(
      `El modelo admite un máximo de ${caps.maxScenes} escenas (detectadas: ${scenes.length}).`
    );
  }

  // Validar si alguna escena tiene prompt vacío
  scenes.forEach((s, idx) => {
    if (!s.prompt || !s.prompt.trim()) {
      errors.push(`La Escena ${s.index || idx + 1} no tiene descripción.`);
    }
    const maxP = caps.maxPromptPerScene || 512;
    if ((s.prompt || "").length > maxP) {
      errors.push(
        `La Escena ${s.index || idx + 1} excede el límite de ${maxP} caracteres (${s.prompt.length} caracteres).`
      );
    }
  });

  // Validar duración total
  if (caps.durations && caps.durations.length > 0) {
    if (!caps.durations.includes(totalDuration)) {
      const closest = caps.durations.reduce((prev, curr) =>
        Math.abs(curr - totalDuration) < Math.abs(prev - totalDuration) ? curr : prev
      );
      errors.push(
        `La suma de duraciones (${totalDuration}s) no coincide con una duración válida para este modelo (${caps.durations.join(", ")}s). Sugerencia: ajustar a ${closest}s.`
      );
    } else if (selectedDuration && totalDuration !== selectedDuration) {
      warnings.push(
        `La duración total de las escenas (${totalDuration}s) difiere de la duración seleccionada en el panel (${selectedDuration}s).`
      );
    }
  }

  return {
    isValid: errors.length === 0,
    strategy: caps.strategy,
    caps,
    errors,
    warnings,
    totalDuration,
  };
}
